// Consultas de Compras (docs/108 B6): las bandejas de OC, recepciones, facturas, NC/ND y devoluciones;
// proveedores; costos de compra; historial por proveedor y la bandeja de aprobaciones (lo que espera
// la firma de OTRA persona). Enter o doble clic abre el documento en su pestaña.
import { useState } from 'react';
import type { ClienteEncontrado } from '../api/contratos';
import { useBuscarClientes, useDebounce } from '../api/consultas';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { decimal, fecha, fechaHora, pesos, rut } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { useBodegas } from '../stock/PantallasStock';
import { BuscadorCliente } from '../ventas/BuscadorCliente';
import { DialogoMotivo, NombreProveedor } from './comun';
import {
  NOMBRE_ESTADO_DEVOLUCION,
  NOMBRE_ESTADO_DOC,
  NOMBRE_ESTADO_OC,
  NOMBRE_ESTADO_RECEPCION,
  NOMBRE_TIPO_COMPRA,
  useAccionCompras,
  useBandejaCompras,
  useCostosCompra,
  useDevoluciones,
  useFacturasCompra,
  useHistorialCosto,
  useNotasCompra,
  useOrdenesCompra,
  useRecepciones,
  type CostoDeProducto,
  type DevolucionListada,
  type DocumentoListado,
  type EstadoDevolucion,
  type EstadoDocumento,
  type EstadoOrden,
  type EstadoRecepcion,
  type ExcepcionRecepcion,
  type OrdenListada,
  type RecepcionListada,
  type SolicitudCorreccion,
  type SolicitudReversa,
} from './datos';

type Abrir = (id: string, titulo: string) => void;

function SelectorEstado<T extends string>({
  etiqueta,
  nombres,
  valor,
  alCambiar,
}: {
  etiqueta: string;
  nombres: Record<T, string>;
  valor: T | '';
  alCambiar: (v: T | '') => void;
}) {
  return (
    <label className="campo">
      <span className="etiqueta">Estado</span>
      <select
        aria-label={etiqueta}
        value={valor}
        onChange={(e) => alCambiar(e.target.value as T | '')}
      >
        <option value="">Todos</option>
        {(Object.keys(nombres) as T[]).map((k) => (
          <option key={k} value={k}>
            {nombres[k]}
          </option>
        ))}
      </select>
    </label>
  );
}

function FiltroProveedor({
  valor,
  alCambiar,
}: {
  valor: { id: string; razonSocial: string } | null;
  alCambiar: (p: { id: string; razonSocial: string } | null) => void;
}) {
  const { empresa } = useSesion();
  const [buscando, setBuscando] = useState(false);
  return (
    <div className="campo">
      <span className="etiqueta">Proveedor</span>
      <span className="acciones-fila">
        <button
          type="button"
          className="selector-cliente"
          aria-label="Filtrar por proveedor"
          onClick={() => setBuscando(true)}
        >
          {valor ? valor.razonSocial : 'Todos'}
        </button>
        {valor && (
          <button
            type="button"
            className="boton-mini"
            aria-label="Quitar proveedor"
            onClick={() => alCambiar(null)}
          >
            ✕
          </button>
        )}
      </span>
      {buscando && (
        <BuscadorCliente
          empresaId={empresa!.id}
          rol="proveedor"
          alElegir={(c) => {
            alCambiar({ id: c.id, razonSocial: c.razon_social });
            setBuscando(false);
          }}
          alCerrar={() => setBuscando(false)}
        />
      )}
    </div>
  );
}

// ================================================================ órdenes de compra

export const columnasOc: Columna<OrdenListada>[] = [
  {
    id: 'n',
    titulo: 'N°',
    celda: (o) => <span className="mono">{o.numero_visible}</span>,
    ancho: '8rem',
  },
  { id: 'f', titulo: 'Fecha', celda: (o) => fecha(o.fecha_negocio), ancho: '7rem' },
  { id: 'p', titulo: 'Proveedor', celda: (o) => o.proveedor_razon_social },
  { id: 'e', titulo: 'Entrega', celda: (o) => fecha(o.fecha_entrega_esperada), ancho: '7rem' },
  { id: 'neto', titulo: 'Neto', celda: (o) => pesos(o.total_neto), alinear: 'der', ancho: '8rem' },
  { id: 'est', titulo: 'Estado', celda: (o) => NOMBRE_ESTADO_OC[o.estado], ancho: '8rem' },
];

export function OrdenesCompra({
  alAbrir,
  alNueva,
  estadoInicial,
}: {
  alAbrir: Abrir;
  alNueva: () => void;
  estadoInicial?: string | undefined;
}) {
  const { empresa, puede } = useSesion();
  const [estado, setEstado] = useState<EstadoOrden | ''>((estadoInicial as EstadoOrden) ?? '');
  const [texto, setTexto] = useState('');
  const [proveedor, setProveedor] = useState<{ id: string; razonSocial: string } | null>(null);
  const [pagina, setPagina] = useState(1);
  const q = useOrdenesCompra(empresa!.id, {
    estado: estado || undefined,
    texto: useDebounce(texto, 300),
    proveedorEntidadId: proveedor?.id,
    pagina,
  });
  return (
    <div className="pantalla consulta" data-pantalla="ordenes-compra">
      <div className="barra-filtros">
        <SelectorEstado
          etiqueta="Estado de la OC"
          nombres={NOMBRE_ESTADO_OC}
          valor={estado}
          alCambiar={(v) => {
            setEstado(v);
            setPagina(1);
          }}
        />
        <FiltroProveedor
          valor={proveedor}
          alCambiar={(p) => {
            setProveedor(p);
            setPagina(1);
          }}
        />
        <label className="campo">
          <span className="etiqueta">Buscar</span>
          <input
            aria-label="Buscar OC"
            placeholder="Proveedor, SKU o producto…"
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              setPagina(1);
            }}
          />
        </label>
        <span className="espaciador" />
        {puede('compras.oc.crear') && (
          <button type="button" className="primario" onClick={alNueva}>
            Nueva OC
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Órdenes de compra"
        columnas={columnasOc}
        filas={q.data?.items ?? []}
        clave={(o) => o.id}
        cargando={q.isLoading}
        vacio="Sin órdenes de compra"
        alAbrir={(o) => alAbrir(o.id, o.numero_visible)}
        paginacion={{ pagina, hayMas: (q.data?.items.length ?? 0) >= 50, alCambiar: setPagina }}
        autofoco
      />
    </div>
  );
}

// ================================================================ recepciones

function useNombreBodega() {
  const { empresa } = useSesion();
  const b = useBodegas(empresa!.id);
  return (id: string) => {
    const x = b.lista.find((y) => y.id === id);
    return x ? `${x.codigo} · ${x.nombre}` : '';
  };
}

export function Recepciones({
  alAbrir,
  alNuevaSinOc,
}: {
  alAbrir: Abrir;
  alNuevaSinOc: () => void;
}) {
  const { empresa, puede } = useSesion();
  const [estado, setEstado] = useState<EstadoRecepcion | ''>('');
  const [proveedor, setProveedor] = useState<{ id: string; razonSocial: string } | null>(null);
  const [pagina, setPagina] = useState(1);
  const bodega = useNombreBodega();
  const q = useRecepciones(empresa!.id, {
    estado: estado || undefined,
    proveedorEntidadId: proveedor?.id,
    pagina,
  });
  const columnas: Columna<RecepcionListada>[] = [
    {
      id: 'n',
      titulo: 'N°',
      celda: (r) => <span className="mono">{r.numero_visible ?? 'borrador'}</span>,
      ancho: '8rem',
    },
    { id: 'f', titulo: 'Fecha', celda: (r) => fecha(r.fecha_negocio), ancho: '7rem' },
    { id: 'p', titulo: 'Proveedor', celda: (r) => <NombreProveedor id={r.proveedor_entidad_id} /> },
    {
      id: 'm',
      titulo: 'Modalidad',
      celda: (r) => (r.modalidad === 'CONTRA_OC' ? 'Contra OC' : 'Sin OC'),
      ancho: '7rem',
    },
    { id: 'b', titulo: 'Bodega', celda: (r) => bodega(r.ubicacion_id), ancho: '12rem' },
    { id: 'est', titulo: 'Estado', celda: (r) => NOMBRE_ESTADO_RECEPCION[r.estado], ancho: '8rem' },
  ];
  return (
    <div className="pantalla consulta" data-pantalla="recepciones">
      <div className="barra-filtros">
        <SelectorEstado
          etiqueta="Estado de la recepción"
          nombres={NOMBRE_ESTADO_RECEPCION}
          valor={estado}
          alCambiar={(v) => {
            setEstado(v);
            setPagina(1);
          }}
        />
        <FiltroProveedor
          valor={proveedor}
          alCambiar={(p) => {
            setProveedor(p);
            setPagina(1);
          }}
        />
        <span className="espaciador" />
        <span className="apagado">
          Para recibir contra una OC, ábrela y usa «Recibir mercadería».
        </span>
        {puede('compras.recepcion.crear') && (
          <button type="button" onClick={alNuevaSinOc}>
            Recepción sin OC
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Recepciones"
        columnas={columnas}
        filas={q.data?.items ?? []}
        clave={(r) => r.id}
        cargando={q.isLoading}
        vacio="Sin recepciones"
        alAbrir={(r) => alAbrir(r.id, r.numero_visible ?? 'Recepción')}
        paginacion={{ pagina, hayMas: (q.data?.items.length ?? 0) >= 50, alCambiar: setPagina }}
        autofoco
      />
    </div>
  );
}

// ================================================================ facturas y notas

const columnasDoc: Columna<DocumentoListado>[] = [
  {
    id: 'n',
    titulo: 'Documento',
    celda: (d) => (
      <span className="mono">{d.numero_visible ?? `${d.tipo_sii ?? ''} ${d.folio ?? ''}`}</span>
    ),
    ancho: '9rem',
  },
  {
    id: 't',
    titulo: 'Tipo',
    celda: (d) => (d.tipo_sii ? (NOMBRE_TIPO_COMPRA[d.tipo_sii] ?? d.tipo_sii) : 'No tributario'),
    ancho: '12rem',
  },
  { id: 'f', titulo: 'Emisión', celda: (d) => fecha(d.fecha_emision), ancho: '7rem' },
  { id: 'p', titulo: 'Proveedor', celda: (d) => d.razon_social_emisor },
  {
    id: 'rut',
    titulo: 'RUT',
    celda: (d) => <span className="mono">{rut(d.rut_emisor)}</span>,
    ancho: '8rem',
  },
  {
    id: 'tot',
    titulo: 'Total',
    celda: (d) => <b>{pesos(d.monto_total)}</b>,
    alinear: 'der',
    ancho: '8rem',
  },
  {
    id: 'est',
    titulo: 'Estado',
    // 0131: el ingreso sin factura anulado o corregido.
    celda: (d) => (d.invalidada ? 'Anulado' : NOMBRE_ESTADO_DOC[d.estado]),
    ancho: '7rem',
  },
];

export function FacturasCompra({ alAbrir, alNueva }: { alAbrir: Abrir; alNueva: () => void }) {
  const { empresa, puede } = useSesion();
  const [estado, setEstado] = useState<EstadoDocumento | ''>('');
  const [folio, setFolio] = useState('');
  const [proveedor, setProveedor] = useState<{ id: string; razonSocial: string } | null>(null);
  const [pagina, setPagina] = useState(1);
  const q = useFacturasCompra(empresa!.id, {
    estado: estado || undefined,
    proveedorEntidadId: proveedor?.id,
    folio: useDebounce(folio, 300),
    pagina,
  });
  // El listado de documentos trae también las NC/ND: aquí se ven solo las facturas.
  const filas = (q.data?.items ?? []).filter(
    (d) => d.tipo_sii === null || ![55, 56, 60, 61].includes(d.tipo_sii),
  );
  return (
    <div className="pantalla consulta" data-pantalla="facturas-compra">
      <div className="barra-filtros">
        <SelectorEstado
          etiqueta="Estado de la factura"
          nombres={NOMBRE_ESTADO_DOC}
          valor={estado}
          alCambiar={(v) => {
            setEstado(v);
            setPagina(1);
          }}
        />
        <FiltroProveedor
          valor={proveedor}
          alCambiar={(p) => {
            setProveedor(p);
            setPagina(1);
          }}
        />
        <label className="campo campo-corto">
          <span className="etiqueta">Folio</span>
          <input aria-label="Folio" value={folio} onChange={(e) => setFolio(e.target.value)} />
        </label>
        <span className="espaciador" />
        {puede('compras.factura.crear') && (
          <button type="button" className="primario" onClick={alNueva}>
            Registrar factura
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Facturas de compra"
        columnas={columnasDoc}
        filas={filas}
        clave={(d) => d.id}
        cargando={q.isLoading}
        vacio="Sin facturas"
        alAbrir={(d) => alAbrir(d.id, d.numero_visible ?? `FCC ${d.folio}`)}
        paginacion={{ pagina, hayMas: (q.data?.items.length ?? 0) >= 50, alCambiar: setPagina }}
        autofoco
      />
    </div>
  );
}

export function NotasCompra({ alAbrir, alNueva }: { alAbrir: Abrir; alNueva: () => void }) {
  const { empresa, puede } = useSesion();
  const [estado, setEstado] = useState<EstadoDocumento | ''>('');
  const [clase, setClase] = useState<'NOTA_CREDITO' | 'NOTA_DEBITO' | ''>('');
  const [proveedor, setProveedor] = useState<{ id: string; razonSocial: string } | null>(null);
  const q = useNotasCompra(empresa!.id, {
    estado: estado || undefined,
    clase: clase || undefined,
    proveedorEntidadId: proveedor?.id,
  });
  return (
    <div className="pantalla consulta" data-pantalla="notas-compra">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Clase</span>
          <select
            aria-label="Clase de nota"
            value={clase}
            onChange={(e) => setClase(e.target.value as typeof clase)}
          >
            <option value="">NC y ND</option>
            <option value="NOTA_CREDITO">Notas de crédito</option>
            <option value="NOTA_DEBITO">Notas de débito</option>
          </select>
        </label>
        <SelectorEstado
          etiqueta="Estado de la nota"
          nombres={NOMBRE_ESTADO_DOC}
          valor={estado}
          alCambiar={setEstado}
        />
        <FiltroProveedor valor={proveedor} alCambiar={setProveedor} />
        <span className="espaciador" />
        {puede('compras.factura.crear') && (
          <button type="button" className="primario" onClick={alNueva}>
            Registrar NC / ND
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Notas de crédito y débito de proveedores"
        columnas={columnasDoc}
        filas={q.data?.items ?? []}
        clave={(d) => d.id}
        cargando={q.isLoading}
        vacio="Sin notas"
        alAbrir={(d) =>
          alAbrir(d.id, `${d.clase_documento === 'NOTA_DEBITO' ? 'NDC' : 'NCC'} ${d.folio ?? ''}`)
        }
        autofoco
      />
    </div>
  );
}

// ================================================================ devoluciones

export function DevolucionesCompra({ alAbrir }: { alAbrir: Abrir }) {
  const { empresa } = useSesion();
  const [estado, setEstado] = useState<EstadoDevolucion | ''>('');
  const bodega = useNombreBodega();
  const q = useDevoluciones(empresa!.id, { estado: estado || undefined });
  const columnas: Columna<DevolucionListada>[] = [
    {
      id: 'n',
      titulo: 'N°',
      celda: (d) => (
        <span className="mono">{d.numero ? `DEV-${d.numero.padStart(6, '0')}` : 'borrador'}</span>
      ),
      ancho: '8rem',
    },
    { id: 'f', titulo: 'Fecha', celda: (d) => fecha(d.fecha_negocio), ancho: '7rem' },
    { id: 'p', titulo: 'Proveedor', celda: (d) => <NombreProveedor id={d.proveedor_entidad_id} /> },
    { id: 'b', titulo: 'Bodega', celda: (d) => bodega(d.ubicacion_id), ancho: '12rem' },
    { id: 'm', titulo: 'Motivo', celda: (d) => d.motivo },
    {
      id: 'est',
      titulo: 'Estado',
      celda: (d) => NOMBRE_ESTADO_DEVOLUCION[d.estado],
      ancho: '8rem',
    },
  ];
  return (
    <div className="pantalla consulta" data-pantalla="devoluciones-compra">
      <div className="barra-filtros">
        <SelectorEstado
          etiqueta="Estado de la devolución"
          nombres={NOMBRE_ESTADO_DEVOLUCION}
          valor={estado}
          alCambiar={setEstado}
        />
        <span className="espaciador" />
        <span className="apagado">
          Una devolución nueva se crea desde su recepción («Devolver al proveedor»).
        </span>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Devoluciones a proveedor"
        columnas={columnas}
        filas={q.data?.items ?? []}
        clave={(d) => d.id}
        cargando={q.isLoading}
        vacio="Sin devoluciones"
        alAbrir={(d) => alAbrir(d.id, d.numero ? `DEV-${d.numero.padStart(6, '0')}` : 'Devolución')}
        autofoco
      />
    </div>
  );
}

// ================================================================ proveedores

export function ProveedoresCompras({
  alHistorial,
  alFicha,
}: {
  alHistorial: (id: string, razonSocial: string) => void;
  /** Abre la ficha de la entidad (ahí se marca si es cliente, proveedor o ambos). */
  alFicha?: (c: { id: string; razon_social: string }) => void;
}) {
  const { empresa } = useSesion();
  const [texto, setTexto] = useState('');
  const r = useBuscarClientes(empresa!.id, useDebounce(texto), 'razon_social', 'proveedor');
  return (
    <div className="pantalla consulta" data-pantalla="proveedores">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Buscar</span>
          <input
            aria-label="Buscar proveedor"
            autoFocus
            placeholder="RUT o razón social (2 letras o más)…"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <span className="espaciador" />
        <span className="apagado">
          Enter abre la ficha del proveedor (deuda, compras, guías y pagos).
        </span>
      </div>
      {r.isError && <PanelError error={r.error} />}
      <Grilla
        etiqueta="Proveedores"
        columnas={[
          {
            id: 'rut',
            titulo: 'RUT',
            celda: (c) => <span className="mono">{rut(c.rut, c.dv)}</span>,
            ancho: '9rem',
          },
          { id: 'rs', titulo: 'Razón social', celda: (c) => c.razon_social },
          { id: 'fan', titulo: 'Nombre de fantasía', celda: (c) => c.nombre_fantasia ?? '' },
          {
            id: 'cond',
            titulo: 'Condición',
            celda: (c) =>
              c.condicion_pago
                ? `${c.condicion_pago}${c.plazo_dias ? ` · ${c.plazo_dias} d` : ''}`
                : '',
            ancho: '9rem',
          },
          {
            id: 'tipo',
            titulo: 'Tipo',
            celda: (c) => (c.cliente_empresa ? 'Cliente y proveedor' : 'Proveedor'),
            ancho: '10rem',
          },
          { id: 'est', titulo: 'Estado', celda: (c) => c.estado, ancho: '6rem' },
          ...(alFicha
            ? [
                {
                  id: 'ficha',
                  titulo: '',
                  celda: (c: ClienteEncontrado) => (
                    <button
                      type="button"
                      className="boton-mini"
                      aria-label={`Ficha de ${c.razon_social}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        alFicha(c);
                      }}
                    >
                      Ficha
                    </button>
                  ),
                  ancho: '5rem',
                },
              ]
            : []),
        ]}
        filas={r.filas}
        clave={(c) => c.id}
        cargando={r.isFetching}
        vacio={texto.trim().length < 2 ? 'Escribe para buscar' : 'Sin proveedores'}
        alAbrir={(c) => (alFicha ? alFicha(c) : alHistorial(c.id, c.razon_social))}
        mas={{ hayMas: r.hayMas, cargando: r.cargandoMas, alCargar: r.cargarMas }}
      />
    </div>
  );
}

// ================================================================ costos

// Costo neto y bruto por unidad base (2026-10-19): neto + flete, y además IVA e impuestos adicionales.
type ConCosto = {
  costoBase: string;
  fleteBase?: string;
  costoNeto?: string;
  ivaBase?: string;
  impuestosBase?: string;
  costoBruto?: string;
  impuestosAproximados?: boolean;
};
const neto = (c: ConCosto) => c.costoNeto ?? c.costoBase;
const bruto = (c: ConCosto) => c.costoBruto ?? c.costoBase;

export function CostosCompra() {
  const { empresa } = useSesion();
  const [texto, setTexto] = useState('');
  const [proveedor, setProveedor] = useState<{ id: string; razonSocial: string } | null>(null);
  const [sel, setSel] = useState<CostoDeProducto | null>(null);
  const q = useCostosCompra(empresa!.id, {
    texto: useDebounce(texto, 300),
    proveedorEntidadId: proveedor?.id,
  });
  const historia = useHistorialCosto(empresa!.id, sel?.productoId ?? null);
  const variacion = (c: CostoDeProducto) => {
    if (!c.anterior) return '';
    const a = Number(neto(c.anterior));
    if (!a) return '';
    const v = ((Number(neto(c.ultima)) - a) / a) * 100;
    return `${v > 0 ? '+' : ''}${v.toFixed(1).replace('.', ',')} %`;
  };
  return (
    <div className="pantalla consulta" data-pantalla="costos-compra">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Producto</span>
          <input
            aria-label="Buscar producto"
            placeholder="SKU o descripción…"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <FiltroProveedor valor={proveedor} alCambiar={setProveedor} />
        <span className="espaciador" />
        <span className="apagado">
          Por unidad base, según facturas ACEPTADAS. Neto = precio + flete; bruto = neto + IVA +
          impuestos adicionales (ILA). El costo de inventario (PMP) está en Stock › Valorización.
        </span>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Costos de compra"
        columnas={[
          {
            id: 'sku',
            titulo: 'SKU',
            celda: (c) => <span className="mono">{c.sku ?? ''}</span>,
            ancho: '8rem',
          },
          { id: 'd', titulo: 'Descripción', celda: (c) => c.descripcion },
          {
            id: 'u',
            titulo: 'Costo neto',
            celda: (c) => <b>{pesos(neto(c.ultima))}</b>,
            alinear: 'der',
            ancho: '8rem',
          },
          {
            id: 'b',
            titulo: 'Costo bruto',
            celda: (c) => pesos(bruto(c.ultima)),
            alinear: 'der',
            ancho: '8rem',
          },
          { id: 'uf', titulo: 'Fecha', celda: (c) => fecha(c.ultima.fechaEmision), ancho: '7rem' },
          { id: 'up', titulo: 'Proveedor', celda: (c) => c.ultima.proveedor, ancho: '14rem' },
          {
            id: 'a',
            titulo: 'Anterior',
            celda: (c) => (c.anterior ? pesos(neto(c.anterior)) : ''),
            alinear: 'der',
            ancho: '8rem',
          },
          { id: 'v', titulo: 'Var.', celda: variacion, alinear: 'der', ancho: '5rem' },
          {
            id: 'r',
            titulo: 'Mín – máx',
            celda: (c) => `${pesos(c.costoMinimo)} – ${pesos(c.costoMaximo)}`,
            ancho: '11rem',
          },
          { id: 'n', titulo: 'Compras', celda: (c) => c.compras, alinear: 'der', ancho: '5rem' },
        ]}
        filas={q.data ?? []}
        clave={(c) => c.productoId}
        cargando={q.isLoading}
        vacio="Sin compras aceptadas"
        alSeleccionar={setSel}
      />
      {sel && (
        <>
          <h3 className="titulo-seccion">
            Historia de precios · {sel.sku} {sel.descripcion}
          </h3>
          <table className="grilla compacta" aria-label="Historia de precios">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Documento</th>
                <th>Proveedor</th>
                <th className="der">Cantidad</th>
                <th className="der">Precio</th>
                <th className="der">Neto unit.</th>
                <th className="der">Flete</th>
                <th className="der">Costo neto</th>
                <th className="der">IVA</th>
                <th className="der">Imp. adic.</th>
                <th className="der">Costo bruto</th>
              </tr>
            </thead>
            <tbody>
              {(historia.data ?? []).map((h, i) => (
                <tr key={`${h.documentoId}-${i}`}>
                  <td>{fecha(h.fechaEmision)}</td>
                  <td className="mono">
                    {h.tipoSii ?? 'ISF'} · {h.folio}
                  </td>
                  <td>{h.proveedor}</td>
                  <td className="der">{decimal(h.cantidad)}</td>
                  <td className="der">{pesos(h.precioUnitario)}</td>
                  <td className="der">{pesos(h.costoBase)}</td>
                  <td className="der">{pesos((h as ConCosto).fleteBase ?? '0')}</td>
                  <td className="der">
                    <b>{pesos(neto(h))}</b>
                  </td>
                  <td className="der">{pesos((h as ConCosto).ivaBase ?? '0')}</td>
                  <td
                    className="der"
                    title={
                      (h as ConCosto).impuestosAproximados
                        ? 'Venía solo en la cabecera: repartido por valor'
                        : undefined
                    }
                  >
                    {pesos((h as ConCosto).impuestosBase ?? '0')}
                    {(h as ConCosto).impuestosAproximados ? ' ≈' : ''}
                  </td>
                  <td className="der">
                    <b>{pesos(bruto(h))}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// ================================================================ historial por proveedor

export function HistorialCompras({
  proveedorInicial,
  alAbrirOc,
  alAbrirRecepcion,
  alAbrirFactura,
  alAbrirNota,
  alAbrirDevolucion,
}: {
  proveedorInicial?: { id: string; razonSocial: string } | undefined;
  alAbrirOc: Abrir;
  alAbrirRecepcion: Abrir;
  alAbrirFactura: Abrir;
  alAbrirNota: Abrir;
  alAbrirDevolucion: Abrir;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const [proveedor, setProveedor] = useState(proveedorInicial ?? null);
  const pid = proveedor?.id;
  const oc = useOrdenesCompra(empresaId, { proveedorEntidadId: pid });
  const rec = useRecepciones(empresaId, { proveedorEntidadId: pid });
  const fac = useFacturasCompra(empresaId, { proveedorEntidadId: pid });
  const dev = useDevoluciones(empresaId, {});
  const listo = !!pid;
  const facturas = (fac.data?.items ?? []).filter(
    (d) => d.tipo_sii === null || ![55, 56, 60, 61].includes(d.tipo_sii),
  );
  const notas = (fac.data?.items ?? []).filter(
    (d) => d.tipo_sii !== null && [55, 56, 60, 61].includes(d.tipo_sii),
  );
  const devoluciones = (dev.data?.items ?? []).filter((d) => d.proveedor_entidad_id === pid);
  const aceptado = facturas
    .filter((d) => d.estado === 'ACEPTADA')
    .reduce((s, d) => s + Number(d.monto_total), 0);
  return (
    <div className="pantalla consulta" data-pantalla="historial-compras">
      <div className="barra-filtros">
        <FiltroProveedor valor={proveedor} alCambiar={setProveedor} />
        <span className="espaciador" />
        {listo && (
          <span>
            {oc.data?.total ?? 0} OC · {rec.data?.total ?? 0} recepciones · facturado aceptado{' '}
            <b>{pesos(String(aceptado))}</b>
          </span>
        )}
      </div>
      {!listo && <p className="apagado">Elige un proveedor para ver todo lo comprado.</p>}
      {listo && (
        <div className="cuerpo-od">
          <section>
            <h3>Órdenes de compra</h3>
            <Grilla
              etiqueta="OC del proveedor"
              columnas={columnasOc.filter((c) => c.id !== 'p')}
              filas={oc.data?.items ?? []}
              clave={(o) => o.id}
              vacio="Sin OC"
              alAbrir={(o) => alAbrirOc(o.id, o.numero_visible)}
            />
            <h3>Recepciones</h3>
            <Grilla
              etiqueta="Recepciones del proveedor"
              columnas={[
                {
                  id: 'n',
                  titulo: 'N°',
                  celda: (r) => r.numero_visible ?? 'borrador',
                  ancho: '8rem',
                },
                { id: 'f', titulo: 'Fecha', celda: (r) => fecha(r.fecha_negocio), ancho: '7rem' },
                { id: 'e', titulo: 'Estado', celda: (r) => NOMBRE_ESTADO_RECEPCION[r.estado] },
              ]}
              filas={rec.data?.items ?? []}
              clave={(r) => r.id}
              vacio="Sin recepciones"
              alAbrir={(r) => alAbrirRecepcion(r.id, r.numero_visible ?? 'Recepción')}
            />
          </section>
          <section>
            <h3>Facturas</h3>
            <Grilla
              etiqueta="Facturas del proveedor"
              columnas={columnasDoc.filter((c) => c.id !== 'p' && c.id !== 'rut')}
              filas={facturas}
              clave={(d) => d.id}
              vacio="Sin facturas"
              alAbrir={(d) => alAbrirFactura(d.id, d.numero_visible ?? 'Factura')}
            />
            <h3>NC / ND</h3>
            <Grilla
              etiqueta="Notas del proveedor"
              columnas={columnasDoc.filter((c) => c.id !== 'p' && c.id !== 'rut')}
              filas={notas}
              clave={(d) => d.id}
              vacio="Sin notas"
              alAbrir={(d) => alAbrirNota(d.id, d.numero_visible ?? 'Nota')}
            />
            <h3>Devoluciones</h3>
            <Grilla
              etiqueta="Devoluciones del proveedor"
              columnas={[
                { id: 'n', titulo: 'N°', celda: (d) => d.numero ?? 'borrador', ancho: '6rem' },
                { id: 'f', titulo: 'Fecha', celda: (d) => fecha(d.fecha_negocio), ancho: '7rem' },
                { id: 'e', titulo: 'Estado', celda: (d) => NOMBRE_ESTADO_DEVOLUCION[d.estado] },
              ]}
              filas={devoluciones}
              clave={(d) => d.id}
              vacio="Sin devoluciones"
              alAbrir={(d) => alAbrirDevolucion(d.id, 'Devolución')}
            />
          </section>
        </div>
      )}
    </div>
  );
}

// ================================================================ bandeja de aprobaciones

type Resolver = {
  ruta: string;
  titulo: string;
  boton: string;
  conVersion: number | null;
  minimo?: number;
} | null;

export function AprobacionesCompras({
  alAbrirOc,
  alAbrirRecepcion,
  alAbrirFactura,
  alAbrirDevolucion,
}: {
  alAbrirOc: Abrir;
  alAbrirRecepcion: Abrir;
  alAbrirFactura: Abrir;
  alAbrirDevolucion: Abrir;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const ocs = useOrdenesCompra(empresaId, { estado: 'EN_APROBACION' });
  const devs = useDevoluciones(empresaId, { estado: 'PENDIENTE_APROBACION' });
  const reversas = useBandejaCompras<SolicitudReversa>(empresaId, '/compras/solicitudes-reversa');
  const excepciones = useBandejaCompras<ExcepcionRecepcion>(
    empresaId,
    '/compras/excepciones-recepcion',
  );
  const correcciones = useBandejaCompras<SolicitudCorreccion>(
    empresaId,
    '/compras/solicitudes-correccion',
  );
  const accion = useAccionCompras(empresaId);
  const [resolver, setResolver] = useState<Resolver>(null);
  const botones = (aprobar: Resolver, rechazar: Resolver, abrir: () => void) => (
    <span className="acciones-fila">
      <button type="button" className="boton-mini" onClick={abrir}>
        Ver
      </button>
      {aprobar && (
        <button type="button" className="boton-mini primario" onClick={() => setResolver(aprobar)}>
          Aprobar
        </button>
      )}
      {rechazar && (
        <button type="button" className="boton-mini" onClick={() => setResolver(rechazar)}>
          Rechazar
        </button>
      )}
    </span>
  );
  return (
    <div className="pantalla consulta" data-pantalla="aprobaciones-compras">
      <p className="apagado">
        Lo que espera la firma de OTRA persona: quien pidió algo no lo aprueba (el sistema lo
        impide).
      </p>
      <h3 className="titulo-seccion">Órdenes de compra en aprobación</h3>
      <Grilla
        etiqueta="OC en aprobación"
        columnas={[
          ...columnasOc,
          {
            id: 'acc',
            titulo: '',
            celda: (o) =>
              botones(
                puede('compras.oc.aprobar')
                  ? {
                      ruta: `/compras/ordenes/${o.id}/aprobar`,
                      titulo: `Aprobar ${o.numero_visible}`,
                      boton: 'Aprobar',
                      conVersion: o.version,
                    }
                  : null,
                puede('compras.oc.rechazar')
                  ? {
                      ruta: `/compras/ordenes/${o.id}/rechazar`,
                      titulo: `Rechazar ${o.numero_visible}`,
                      boton: 'Rechazar',
                      conVersion: o.version,
                    }
                  : null,
                () => alAbrirOc(o.id, o.numero_visible),
              ),
            ancho: '13rem',
          },
        ]}
        filas={ocs.data?.items ?? []}
        clave={(o) => o.id}
        cargando={ocs.isLoading}
        vacio="Nada por aprobar"
      />
      <h3 className="titulo-seccion">Reversas de recepción solicitadas</h3>
      <Grilla
        etiqueta="Reversas de recepción"
        columnas={[
          { id: 'f', titulo: 'Solicitada', celda: (s) => fechaHora(s.creado_en), ancho: '10rem' },
          { id: 'm', titulo: 'Motivo', celda: (s) => s.motivo },
          {
            id: 'acc',
            titulo: '',
            celda: (s) =>
              botones(
                puede('compras.recepcion.aprobar_reversa')
                  ? {
                      ruta: `/compras/solicitudes-reversa/${s.id}/aprobar`,
                      titulo: 'Aprobar reversa (saca el stock)',
                      boton: 'Aprobar y reversar',
                      conVersion: s.version,
                    }
                  : null,
                puede('compras.recepcion.aprobar_reversa')
                  ? {
                      ruta: `/compras/solicitudes-reversa/${s.id}/rechazar`,
                      titulo: 'Rechazar reversa',
                      boton: 'Rechazar',
                      conVersion: s.version,
                    }
                  : null,
                () => alAbrirRecepcion(s.recepcion_id, 'Recepción'),
              ),
            ancho: '13rem',
          },
        ]}
        filas={reversas.data ?? []}
        clave={(s) => s.id}
        cargando={reversas.isLoading}
        vacio="Nada por aprobar"
      />
      <h3 className="titulo-seccion">Excepciones de recepción (sin OC, sobre-recepción)</h3>
      <Grilla
        etiqueta="Excepciones de recepción"
        columnas={[
          { id: 'f', titulo: 'Fecha', celda: (x) => fechaHora(x.creado_en), ancho: '10rem' },
          { id: 't', titulo: 'Tipo', celda: (x) => x.tipo.toLowerCase().replace(/_/g, ' ') },
          {
            id: 'acc',
            titulo: '',
            celda: (x) =>
              botones(
                puede('compras.recepcion.aprobar_excepcion')
                  ? {
                      ruta: `/compras/excepciones-recepcion/${x.id}/aprobar`,
                      titulo: 'Aprobar excepción',
                      boton: 'Aprobar',
                      conVersion: x.version,
                    }
                  : null,
                puede('compras.recepcion.aprobar_excepcion')
                  ? {
                      ruta: `/compras/excepciones-recepcion/${x.id}/rechazar`,
                      titulo: 'Rechazar excepción',
                      boton: 'Rechazar',
                      conVersion: x.version,
                    }
                  : null,
                () => alAbrirRecepcion(x.recepcion_id, 'Recepción'),
              ),
            ancho: '13rem',
          },
        ]}
        filas={excepciones.data ?? []}
        clave={(x) => x.id}
        cargando={excepciones.isLoading}
        vacio="Nada por aprobar"
      />
      <h3 className="titulo-seccion">Devoluciones por aprobar</h3>
      <Grilla
        etiqueta="Devoluciones por aprobar"
        columnas={[
          { id: 'f', titulo: 'Fecha', celda: (d) => fecha(d.fecha_negocio), ancho: '7rem' },
          {
            id: 'p',
            titulo: 'Proveedor',
            celda: (d) => <NombreProveedor id={d.proveedor_entidad_id} />,
          },
          { id: 'm', titulo: 'Motivo', celda: (d) => d.motivo },
          {
            id: 'acc',
            titulo: '',
            celda: (d) =>
              botones(
                puede('compras.devolucion.aprobar')
                  ? {
                      ruta: `/compras/devoluciones/${d.id}/aprobar`,
                      titulo: 'Aprobar devolución',
                      boton: 'Aprobar',
                      conVersion: d.version,
                    }
                  : null,
                puede('compras.devolucion.aprobar')
                  ? {
                      ruta: `/compras/devoluciones/${d.id}/rechazar`,
                      titulo: 'Rechazar devolución',
                      boton: 'Rechazar',
                      conVersion: d.version,
                    }
                  : null,
                () => alAbrirDevolucion(d.id, 'Devolución'),
              ),
            ancho: '13rem',
          },
        ]}
        filas={devs.data?.items ?? []}
        clave={(d) => d.id}
        cargando={devs.isLoading}
        vacio="Nada por aprobar"
      />
      <h3 className="titulo-seccion">Invalidaciones de factura y reversas de devolución</h3>
      <Grilla
        etiqueta="Solicitudes de corrección"
        columnas={[
          { id: 'f', titulo: 'Solicitada', celda: (s) => fechaHora(s.creado_en), ancho: '10rem' },
          {
            id: 't',
            titulo: 'Tipo',
            celda: (s) =>
              s.tipo === 'INVALIDACION_FACTURA' ? 'Invalidar factura' : 'Reversar devolución',
            ancho: '10rem',
          },
          { id: 'm', titulo: 'Motivo', celda: (s) => s.motivo },
          {
            id: 'acc',
            titulo: '',
            celda: (s) => {
              const permiso =
                s.tipo === 'INVALIDACION_FACTURA'
                  ? 'compras.factura.aprobar_invalidacion'
                  : 'compras.devolucion.aprobar_reversa';
              return botones(
                puede(permiso)
                  ? {
                      ruta: `/compras/solicitudes-correccion/${s.id}/aprobar`,
                      titulo: 'Aprobar y ejecutar',
                      boton: 'Aprobar',
                      conVersion: s.version,
                    }
                  : null,
                puede(permiso)
                  ? {
                      ruta: `/compras/solicitudes-correccion/${s.id}/rechazar`,
                      titulo: 'Rechazar solicitud',
                      boton: 'Rechazar',
                      conVersion: s.version,
                    }
                  : null,
                () =>
                  s.tipo === 'INVALIDACION_FACTURA'
                    ? alAbrirFactura(s.objeto_id, 'Factura')
                    : alAbrirDevolucion(s.objeto_id, 'Devolución'),
              );
            },
            ancho: '13rem',
          },
        ]}
        filas={correcciones.data ?? []}
        clave={(s) => s.id}
        cargando={correcciones.isLoading}
        vacio="Nada por aprobar"
      />
      {accion.isError && !resolver && <PanelError error={accion.error} />}
      {resolver && (
        <DialogoMotivo
          titulo={resolver.titulo}
          textoBoton={resolver.boton}
          minimo={resolver.minimo ?? 3}
          pendiente={accion.isPending}
          error={accion.isError ? accion.error : null}
          alConfirmar={(motivo) =>
            accion.mutate(
              {
                ruta: resolver.ruta,
                cuerpo:
                  resolver.conVersion !== null
                    ? { version: resolver.conVersion, motivo }
                    : { motivo },
              },
              { onSuccess: () => setResolver(null) },
            )
          }
          alCerrar={() => setResolver(null)}
        />
      )}
    </div>
  );
}
